'use client';

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dancing_Script } from 'next/font/google';
import { LOADER_SEEN_CLASS, LOADER_SEEN_KEY } from './loader-session';

// Script face for the wordmark only; the rest of the app stays on Jakarta.
const script = Dancing_Script({ subsets: ['latin'], weight: '700', display: 'swap' });

const EASE = [0.16, 1, 0.3, 1] as const;
const HOLD_MS = 2000;
const P  = '#6D28D9';            // main purple — logo + text
const PR = 'rgba(109,40,217,0.55)'; // leaf veins
const BG = '#F5F3FF';            // near-white lavender background

/* ──────────────────────────────────────────────────────────────────────────────
 * Geometry is measured from the official logo PNG (berry centres, ring radii,
 * calyx lobe/notch/hole/dot ratios, leaf edges) and scaled into a 220 box, so
 * the drawn mark matches the header logo exactly. Stroke 5.3 = the logo's own
 * line weight at this scale (11.5 px on a 92 px ring).
 * ────────────────────────────────────────────────────────────────────────────── */
const STROKE = 5.3;

const BERRIES = [
  { cx: 133.5, cy: 83.9, r: 42.8, delay: 0.08 },
  { cx: 52.8, cy: 128.1, r: 40.0, delay: 0.18 },
  { cx: 126.2, cy: 171.4, r: 33.7, delay: 0.13 },
] as const;

const LEAF_1 = 'M142.1,38.4 C154.2,24.4 170.4,12.3 181.1,10.0 C187.6,17.9 190.4,53.2 177.9,69.5';
const LEAF_2 = 'M178.3,97.4 C194.6,102.5 209.5,123.9 209.5,143.0 C198.3,149.5 169.5,146.2 152.3,126.7';
const VEIN_1 = 'M161.6,47.2 L174.1,25.3';
const VEIN_2 = 'M167.2,116.5 L197.4,136.0';

/** Six rounded lobes: r(θ) blends notch→lobe with a cos(6θ) profile. */
function calyxPath(cx: number, cy: number, r: number): string {
  const lobe = 0.53 * r;
  const notch = 0.41 * r;
  const n = 72;
  const pts: string[] = [];
  for (let i = 0; i < n; i++) {
    const t = (2 * Math.PI * i) / n;
    const w = 0.5 + 0.5 * Math.cos(6 * t);
    const rad = notch + (lobe - notch) * w;
    pts.push(`${(cx + rad * Math.cos(t)).toFixed(1)},${(cy + rad * Math.sin(t)).toFixed(1)}`);
  }
  return `M${pts.join(' L')} Z`;
}

export default function BlueberriesLoader() {
  const [visible, setVisible] = useState(true);
  const [done, setDone] = useState(false);

  useEffect(() => {
    // Repeat page view in this session: the inline <head> script already hid the
    // overlay before first paint (see loader-session.ts), so skip the show.
    if (document.documentElement.classList.contains(LOADER_SEEN_CLASS)) {
      setDone(true);
      return;
    }
    const t = setTimeout(() => setVisible(false), HOLD_MS);
    return () => clearTimeout(t);
  }, []);

  const finish = () => {
    try {
      sessionStorage.setItem(LOADER_SEEN_KEY, '1');
    } catch {
      // Storage unavailable: the loader plays again on the next hard load.
    }
    setDone(true);
  };

  if (done) return null;

  return (
    <AnimatePresence onExitComplete={finish}>
      {visible && (
        <motion.div
          key="loader"
          // Hidden on repeat views, and faded out by CSS if JavaScript never runs (globals.css).
          className="bb-splash"
          exit={{ opacity: 0, scale: 1.012, filter: 'blur(5px)' }}
          transition={{ duration: 0.55, ease: [0.76, 0, 0.24, 1] }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            background: BG,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            userSelect: 'none',
          }}
        >
          {/* Subtle ambient bloom — much softer on the light background */}
          <motion.div
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.3, delay: 0.05, ease: EASE }}
            style={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              marginLeft: -300,
              marginTop: -300,
              width: 600,
              height: 600,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(109,40,217,0.10) 0%, rgba(109,40,217,0.04) 45%, transparent 70%)',
              filter: 'blur(60px)',
              pointerEvents: 'none',
            }}
          />

          {/* Logo — official mark geometry, stroke-drawn in */}
          <div style={{ position: 'relative', zIndex: 10 }}>
            <svg
              viewBox="0 0 220 220"
              width="176"
              height="176"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* ── Berry rings — stroke draw animation ── */}
              {BERRIES.map((b) => (
                <motion.circle
                  key={`ring-${b.cx}`}
                  cx={b.cx}
                  cy={b.cy}
                  r={b.r}
                  stroke={P}
                  strokeWidth={STROKE}
                  strokeLinecap="round"
                  fill="none"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 0.8, delay: b.delay, ease: EASE }}
                />
              ))}

              {/* ── Leaves + veins — stroke draw animation ── */}
              <motion.path
                d={LEAF_1}
                stroke={P}
                strokeWidth={STROKE}
                strokeLinejoin="round"
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.5, delay: 0.6, ease: EASE }}
              />
              <motion.path
                d={VEIN_1}
                stroke={PR}
                strokeWidth={STROKE * 0.75}
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.28, delay: 0.85, ease: EASE }}
              />
              <motion.path
                d={LEAF_2}
                stroke={P}
                strokeWidth={STROKE}
                strokeLinejoin="round"
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.5, delay: 0.68, ease: EASE }}
              />
              <motion.path
                d={VEIN_2}
                stroke={PR}
                strokeWidth={STROKE * 0.75}
                strokeLinecap="round"
                fill="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 0.28, delay: 0.92, ease: EASE }}
              />

              {/* ── Calyx crowns: filled six-lobe star → hole → centre dot ── */}
              {BERRIES.map((b, i) => (
                <motion.g
                  key={`calyx-${b.cx}`}
                  initial={{ opacity: 0, scale: 0.6 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.38, delay: 0.72 + i * 0.05, ease: EASE }}
                  style={{ transformOrigin: `${b.cx}px ${b.cy}px` }}
                >
                  <path d={calyxPath(b.cx, b.cy, b.r)} fill={P} />
                  <circle cx={b.cx} cy={b.cy} r={0.255 * b.r} fill={BG} />
                  <circle cx={b.cx} cy={b.cy} r={0.125 * b.r} fill={P} />
                </motion.g>
              ))}
            </svg>
          </div>

          {/* "blueberries." — brand wordmark fades + drifts up */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 1.05, ease: EASE }}
            style={{
              position: 'absolute',
              bottom: '3rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <span
              className={script.className}
              style={{
                color: P,
                fontSize: '56px',
                lineHeight: 1,
                display: 'block',
                fontWeight: 700,
                letterSpacing: '0.005em',
              }}
            >
              blueberries.
            </span>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
